import { Alert, Button, Checkbox, Loader, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconCheck,
  IconMicrophone,
  IconPlayerStopFilled,
  IconSparkles,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ChosenInvoice, InventoryInvoices } from 'src/Components/Inventory/InventoryInvoices';
import {
  InventoryFormValues,
  InventoryItemFields,
} from 'src/Components/Inventory/InventoryItemFields';
import { formatEuros } from 'src/Components/Inventory/inventoryLabels';
import { InventoryPhotoPicker, PickedPhoto } from 'src/Components/Inventory/InventoryPhotoPicker';
import { useInventoryRooms } from 'src/Components/Inventory/useInventoryRooms';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { useCreateInventoryItem } from 'src/Hooks/Mutations/useInventoryMutations';
import useFetchInventory from 'src/Hooks/Queries/useFetchInventory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import {
  isVoiceRecordingSupported,
  useVoiceRecorder,
  VoiceRecording,
} from 'src/Hooks/Shared/useVoiceRecorder';
import { inventoryApi, memoriesApi } from 'src/Services/API';
import {
  InventoryCreatedDto,
  InventoryDraftDto,
  InventoryValueSource,
  LinkableEntityType,
} from 'src/Services/API/generated';
import { MemoryDatePrecision } from 'src/Services/API/Memories.api';
import { trackInventoryItemCreated } from 'src/Services/Mixpanel/events';
import { formatMemoryDate } from 'src/Utils/memoryDate';

import styles from './InventoryAddPage.module.scss';

type Step = 'room' | 'photos' | 'voice' | 'thinking' | 'review' | 'saved';

// The whole story, all parts together (« Continuer à raconter »).
const VOICE_MAX_SECONDS = 180;
const NO_ROOM = 'none';

const EMPTY_VALUES: InventoryFormValues = {
  name: '',
  roomId: null,
  brandAndModel: '',
  condition: '',
  notes: '',
  quantity: 1,
  purchaseYear: null,
  purchasePrice: null,
  replacementValue: null,
  replacementValueSource: null,
  replacementValueSourceLabel: null,
  replacementValueSourceUrl: null,
  depreciationEnabled: true,
};

const suggestionKey = (type: string, id: string) => `${type}:${id}`;

const formatClock = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * Inventaire express (Alexis, 03/11/2026): the room, a few photos, a few words; Lucien fills
 * the card, the member checks it and saves. Aim: less than a minute per object, on a phone.
 */
const InventoryAddPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  const [searchParams] = useSearchParams();
  const { data: inventory } = useFetchInventory();
  const {
    rooms,
    roomOptions,
    isLoading: isLoadingRooms,
    createRoom,
    isCreatingRoom,
  } = useInventoryRooms();
  const { mutateAsync: createItem, isLoading: isSaving } = useCreateInventoryItem();

  const initialRoom = searchParams.get('roomId');
  const [step, setStep] = useState<Step>(initialRoom ? 'photos' : 'room');
  const [roomChoice, setRoomChoice] = useState<string | null>(initialRoom);
  const [photos, setPhotos] = useState<PickedPhoto[]>([]);
  // The member may stop and go on telling: each part is kept, in order.
  const [recordings, setRecordings] = useState<VoiceRecording[]>([]);
  const [draft, setDraft] = useState<InventoryDraftDto | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [values, setValues] = useState<InventoryFormValues>(EMPTY_VALUES);
  const [invoices, setInvoices] = useState<ChosenInvoice[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [taskTitle, setTaskTitle] = useState<string | null>(null);
  const [memoryTitle, setMemoryTitle] = useState<string | null>(null);
  // Lucien heard a date in the story (« offert en 1950 »): the memory is dated then, unless
  // the member says it is from today.
  const [useToldDate, setUseToldDate] = useState(true);
  const [webPriceAnswered, setWebPriceAnswered] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<InventoryCreatedDto | null>(null);
  const startedAt = useRef(Date.now());
  const [secondsSpent, setSecondsSpent] = useState(0);

  const rule = inventory?.settings ?? { depreciationRatePercent: 10, depreciationMaxPercent: 80 };
  const roomId = roomChoice && roomChoice !== NO_ROOM ? roomChoice : null;
  const roomName = rooms.find(room => room.id === roomId)?.name ?? null;
  const listPath = `/asset/${assetId}/inventories`;
  const language = i18n.language?.startsWith('en') ? 'en' : 'fr';

  const onPhotosChange = useCallback(
    (update: (current: PickedPhoto[]) => PickedPhoto[]) => setPhotos(update),
    []
  );

  const recordedSeconds = recordings.reduce((sum, part) => sum + part.durationSeconds, 0);
  const remainingSeconds = Math.max(0, VOICE_MAX_SECONDS - recordedSeconds);
  const { isRecording, elapsedSeconds, start, stop } = useVoiceRecorder({
    maxDurationSeconds: Math.max(1, remainingSeconds),
    onRecorded: part => setRecordings(current => [...current, part]),
  });

  const startRecording = async () => {
    setHint(null);
    try {
      await start();
    } catch {
      setHint(
        t(
          'inventory.add.micDenied',
          'Le micro n’est pas autorisé. Autorisez-le dans le navigateur, ou passez cette étape.'
        )
      );
    }
  };

  const goTo = (next: Step) => {
    setHint(null);
    setStep(next);
    window.scrollTo({ top: 0 });
  };

  // ---------------------------------------------------------------------------------------
  // Lucien
  // ---------------------------------------------------------------------------------------

  const askLucien = async (parts: VoiceRecording[]) => {
    // Photos keep uploading while the member speaks: Lucien waits for all of them.
    if (photos.some(photo => !photo.serverFile)) {
      setHint(t('inventory.add.photosPending', 'Attendez que les photos soient envoyées.'));
      return;
    }
    goTo('thinking');
    setDraftError(null);
    let result: InventoryDraftDto | null = null;
    try {
      const photoIds = photos.flatMap(photo => (photo.serverFile ? [photo.serverFile.id] : []));
      const files = parts.map(
        part => new File([part.audio], part.filename, { type: part.audio.type })
      );
      result = (
        await inventoryApi.inventoryControllerDraft(
          assetId!,
          photoIds,
          files.length ? files : undefined,
          roomId ?? undefined,
          language
        )
      ).data;
    } catch {
      setDraftError(
        t(
          'inventory.add.draftFailed',
          "Lucien n'a pas pu préparer la fiche. Remplissez-la à la main, c'est rapide."
        )
      );
    }
    if (result && !result.drafted) {
      setDraftError(
        t(
          'inventory.add.draftFailed',
          "Lucien n'a pas pu préparer la fiche. Remplissez-la à la main, c'est rapide."
        )
      );
    }
    applyDraft(result);
    goTo('review');
  };

  const applyDraft = (result: InventoryDraftDto | null) => {
    setDraft(result);
    setValues({
      ...EMPTY_VALUES,
      roomId: result?.roomId ?? roomId,
      name: result?.name ?? '',
      brandAndModel: result?.brandAndModel ?? '',
      condition: result?.condition ?? '',
      quantity: result?.quantity ?? 1,
      purchaseYear: result?.purchaseYear ?? null,
      purchasePrice: result?.purchasePrice ?? null,
      replacementValue: result?.replacementValue ?? null,
      replacementValueSource:
        result?.replacementValue != null && result?.replacementValue !== undefined
          ? InventoryValueSource.Manual
          : null,
    });
    const preselected = (result?.suggestedLinks ?? []).filter(link => link.preselected);
    setChecked(new Set(preselected.map(link => suggestionKey(link.type, link.id))));
    setTaskTitle(result?.suggestedTaskTitle ?? null);
    setMemoryTitle(result?.suggestedMemoryTitle ?? null);
    setUseToldDate(true);
    setWebPriceAnswered(false);
  };

  const [taskChecked, setTaskChecked] = useState(true);
  const [memoryChecked, setMemoryChecked] = useState(true);
  useEffect(() => {
    setTaskChecked(true);
    setMemoryChecked(true);
  }, [draft]);

  const toggle = (key: string) =>
    setChecked(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const acceptWebPrice = () => {
    if (!draft?.suggestedReplacementValue) return;
    setValues(current => ({
      ...current,
      replacementValue: draft.suggestedReplacementValue ?? null,
      replacementValueSource: InventoryValueSource.Lucien,
      replacementValueSourceLabel: draft.suggestedReplacementValueLabel ?? null,
      replacementValueSourceUrl: draft.suggestedReplacementValueUrl ?? null,
    }));
    setWebPriceAnswered(true);
  };

  const createSpokenRoom = async () => {
    if (!draft?.unknownRoomName) return;
    try {
      const room = await createRoom(draft.unknownRoomName);
      setValues(current => ({ ...current, roomId: room.id }));
      setDraft(current => (current ? { ...current, unknownRoomName: null } : current));
    } catch {
      notifications.show({
        color: 'red',
        message: t('inventory.add.roomCreateError', "La pièce n'a pas pu être créée."),
      });
    }
  };

  // ---------------------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------------------

  const documentSuggestions = (draft?.suggestedLinks ?? []).filter(
    link => link.type === LinkableEntityType.VirtualFile
  );
  const otherSuggestions = (draft?.suggestedLinks ?? []).filter(
    link => link.type !== LinkableEntityType.VirtualFile
  );

  const toldDate = draft?.suggestedMemoryTakenAt
    ? formatMemoryDate(
        t,
        draft.suggestedMemoryTakenAt,
        draft.suggestedMemoryTakenAtPrecision as MemoryDatePrecision,
        draft.suggestedMemoryTakenAtApproximate
      )
    : null;

  const save = async () => {
    setError(null);
    if (!values.name.trim()) {
      setError(t('inventory.add.nameRequired', "Donnez un nom à l'objet (ex. « Canapé »)."));
      return;
    }
    if (photos.some(photo => !photo.serverFile)) {
      setError(t('inventory.add.photosPending', 'Attendez que les photos soient envoyées.'));
      return;
    }
    // A pre-ticked invoice-like document is attached as an invoice; others as plain links.
    const invoiceIds = new Set(invoices.map(invoice => invoice.id));
    documentSuggestions
      .filter(link => link.preselected && checked.has(suggestionKey(link.type, link.id)))
      .forEach(link => invoiceIds.add(link.id));
    const links = [...documentSuggestions.filter(link => !link.preselected), ...otherSuggestions]
      .filter(link => checked.has(suggestionKey(link.type, link.id)))
      .map(link => ({ type: link.type, id: link.id }));
    const withTask = !!taskTitle?.trim() && taskChecked;
    const withMemory = !!memoryTitle?.trim() && memoryChecked;

    // Only the creation itself can fail as "not saved": what follows must never invite a
    // second click (duplicate object).
    let item: InventoryCreatedDto;
    try {
      item = await createItem({
        name: values.name.trim(),
        roomId: values.roomId,
        brandAndModel: values.brandAndModel || null,
        condition: values.condition || null,
        notes: values.notes || null,
        quantity: values.quantity,
        purchaseYear: values.purchaseYear,
        purchasePrice: values.purchasePrice,
        replacementValue: values.replacementValue,
        replacementValueSource: values.replacementValueSource,
        replacementValueSourceLabel: values.replacementValueSourceLabel,
        replacementValueSourceUrl: values.replacementValueSourceUrl,
        depreciationEnabled: values.depreciationEnabled,
        photoIds: photos.map(photo => photo.serverFile!.id),
        invoiceFileIds: [...invoiceIds],
        links,
        task: withTask ? { title: taskTitle!.trim() } : undefined,
        memory: withMemory
          ? {
              title: memoryTitle!.trim(),
              description: draft?.transcript ?? undefined,
              ...(toldDate &&
                useToldDate && {
                  takenAt: draft!.suggestedMemoryTakenAt!,
                  takenAtPrecision: draft!.suggestedMemoryTakenAtPrecision,
                  takenAtApproximate: draft!.suggestedMemoryTakenAtApproximate,
                }),
            }
          : undefined,
      });
    } catch {
      setError(
        t(
          'inventory.add.saveError',
          "L'objet n'a pas pu être enregistré. Vérifiez votre connexion et réessayez."
        )
      );
      return;
    }

    const spent = Math.round((Date.now() - startedAt.current) / 1000);
    setSecondsSpent(spent);
    setCreated(item);
    goTo('saved');

    const warnings = [...item.warnings];
    // The voice is kept with the memory, as a story told by the member, part after part. A
    // single part reuses Lucien's transcript; several parts are transcribed one by one.
    if (item.createdMemoryId && recordings.length) {
      try {
        for (const part of recordings) {
          await memoriesApi.addRecording(assetId!, item.createdMemoryId, {
            audio: part.audio,
            filename: part.filename,
            durationSeconds: part.durationSeconds,
            transcript: recordings.length === 1 ? (draft?.transcript ?? undefined) : undefined,
            language,
          });
        }
      } catch {
        warnings.push('RECORDING');
      }
    }
    if (warnings.length) {
      notifications.show({
        color: 'orange',
        message: t(
          'inventory.add.partialSave',
          "L'objet est enregistré, mais l'action ou le souvenir n'a pas pu être créé. Vous pourrez le faire depuis sa fiche."
        ),
      });
    }

    trackInventoryItemCreated({
      drafted_by_lucien: !!draft?.drafted,
      has_voice: recordings.length > 0,
      voice_parts: recordings.length,
      photos_count: photos.length,
      invoices_count: invoiceIds.size,
      value_source:
        values.replacementValueSource === InventoryValueSource.Lucien
          ? 'lucien'
          : values.replacementValue != null
            ? 'manual'
            : 'none',
      created_task: !!item.createdTaskId,
      created_memory: !!item.createdMemoryId,
      seconds_spent: spent,
    });
  };

  const nextObject = (sameRoom: boolean) => {
    const keptRoom = sameRoom ? (created?.room?.id ?? roomId) : null;
    setPhotos([]);
    setRecordings([]);
    setDraft(null);
    setValues(EMPTY_VALUES);
    setInvoices([]);
    setChecked(new Set());
    setCreated(null);
    setError(null);
    startedAt.current = Date.now();
    setRoomChoice(keptRoom ?? (sameRoom ? NO_ROOM : null));
    goTo(sameRoom ? 'photos' : 'room');
  };

  const roomTiles = useMemo(
    () => [
      ...rooms.map(room => ({
        value: room.id,
        label: room.name,
        count: (inventory?.items ?? []).filter(item => item.room?.id === room.id).length,
      })),
      { value: NO_ROOM, label: t('inventory.add.noRoom', 'Sans pièce'), count: null },
    ],
    [rooms, inventory, t]
  );

  // ---------------------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------------------

  const stepLabel = (index: number) =>
    t('inventory.add.stepOf', 'Étape {{index}} sur 3', { index });

  return (
    <div className={styles.container}>
      <Link to={listPath}>
        <Button variant="transparent" leftSection={<IconArrowLeft size={18} />}>
          {t('inventory.back', "Retour à l'inventaire")}
        </Button>
      </Link>

      {step === 'room' && (
        <section className={styles.step}>
          <Text className={styles.stepLabel}>{stepLabel(1)}</Text>
          <PageHeader label={t('inventory.add.roomTitle', 'Dans quelle pièce êtes-vous ?')} />
          <Text className={styles.sub}>
            {t('inventory.add.roomHint', 'On s’en souvient pour les objets suivants.')}
          </Text>
          {isLoadingRooms ? (
            <Loader />
          ) : (
            <div className={styles.tiles}>
              {roomTiles.map(tile => (
                <button
                  key={tile.value}
                  type="button"
                  className={`${styles.tile} ${roomChoice === tile.value ? styles.tileOn : ''}`}
                  aria-pressed={roomChoice === tile.value}
                  onClick={() => {
                    setRoomChoice(tile.value);
                    setHint(null);
                  }}
                >
                  <span className={styles.tileName}>{tile.label}</span>
                  {tile.count !== null && (
                    <span className={styles.tileMeta}>
                      {t('inventory.objects', {
                        count: tile.count,
                        defaultValue: '{{count}} objets',
                      })}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {rooms.length === 0 && !isLoadingRooms && (
            <Text size="sm" c="dimmed">
              {t(
                'inventory.add.noRoomsYet',
                'Aucune pièce n’est encore décrite : choisissez « Sans pièce », Lucien proposera de créer celle que vous direz.'
              )}
            </Text>
          )}
          {hint && <Alert color="orange">{hint}</Alert>}
          <Button
            size="lg"
            fullWidth
            onClick={() =>
              roomChoice
                ? goTo('photos')
                : setHint(t('inventory.add.roomRequired', 'Choisissez une pièce pour continuer.'))
            }
          >
            {t('common.next', 'Suivant')}
          </Button>
        </section>
      )}

      {step === 'photos' && (
        <section className={styles.step}>
          <Text className={styles.stepLabel}>
            {stepLabel(2)}
            {roomName ? ` · ${roomName}` : ''}
          </Text>
          <PageHeader label={t('inventory.add.photosTitle', 'Prenez l’objet en photo')} />
          <Text className={styles.sub}>
            {t(
              'inventory.add.photosHint',
              'De 1 à 5 photos : de face, de près, l’étiquette (Lucien y lit la marque et le modèle), un défaut.'
            )}
          </Text>
          <InventoryPhotoPicker photos={photos} onChange={onPhotosChange} large />
          {hint && <Alert color="orange">{hint}</Alert>}
          <Button
            size="lg"
            fullWidth
            onClick={() => {
              if (!photos.length) {
                setHint(t('inventory.add.photoRequired', 'Prenez au moins une photo de l’objet.'));
                return;
              }
              if (photos.some(photo => photo.failed)) {
                setHint(
                  t(
                    'inventory.add.photoFailed',
                    'Une photo n’a pas pu être envoyée : réessayez-la ou retirez-la.'
                  )
                );
                return;
              }
              if (photos.some(photo => !photo.serverFile)) {
                setHint(
                  t('inventory.add.photosPending', 'Attendez que les photos soient envoyées.')
                );
                return;
              }
              goTo('voice');
            }}
          >
            {photos.length
              ? t('inventory.add.photosDone', 'C’est bon ({{count}})', { count: photos.length })
              : t('inventory.add.photosDoneEmpty', 'C’est bon')}
          </Button>
          <Button variant="outline" size="md" onClick={() => goTo('room')}>
            {t('inventory.add.changeRoom', 'Changer de pièce')}
          </Button>
        </section>
      )}

      {step === 'voice' && (
        <section className={styles.step}>
          <Text className={styles.stepLabel}>{stepLabel(3)}</Text>
          <PageHeader label={t('inventory.add.voiceTitle', 'Parlez-moi de cet objet')} />
          <div className={styles.prompts}>
            {[
              t('inventory.add.promptWhen', 'Quand ?'),
              t('inventory.add.promptWhere', 'Où ?'),
              t('inventory.add.promptPrice', 'Combien ?'),
              t('inventory.add.promptState', 'Son état ?'),
              t('inventory.add.promptMemory', 'Un souvenir ?'),
            ].map(prompt => (
              <span key={prompt} className={styles.prompt}>
                {prompt}
              </span>
            ))}
          </div>
          {isVoiceRecordingSupported() ? (
            <div className={styles.mic}>
              {isRecording ? (
                <Button
                  size="xl"
                  h={96}
                  color="red"
                  leftSection={<IconPlayerStopFilled size={28} />}
                  onClick={stop}
                >
                  {t('inventory.add.stop', 'Arrêter · {{clock}}', {
                    clock: formatClock(recordedSeconds + elapsedSeconds),
                  })}
                </Button>
              ) : remainingSeconds > 0 ? (
                <Button
                  size="xl"
                  h={96}
                  leftSection={<IconMicrophone size={28} />}
                  onClick={startRecording}
                >
                  {recordings.length
                    ? t('inventory.add.recordMore', 'Continuer à raconter')
                    : t('inventory.add.record', 'Appuyez et parlez')}
                </Button>
              ) : (
                <Text className={styles.sub}>
                  {t('inventory.add.voiceFull', 'Les 3 minutes sont utilisées.')}
                </Text>
              )}
              {recordings.length > 0 && !isRecording && (
                <>
                  <Text className={styles.sub}>
                    {recordings.length > 1
                      ? t(
                          'inventory.add.recordedParts',
                          'Enregistré · {{clock}} en {{count}} fois',
                          {
                            clock: formatClock(recordedSeconds),
                            count: recordings.length,
                          }
                        )
                      : t('inventory.add.recorded', 'Enregistré · {{clock}}', {
                          clock: formatClock(recordedSeconds),
                        })}
                  </Text>
                  <Button
                    variant="outline"
                    size="md"
                    onClick={() => {
                      setHint(null);
                      setRecordings([]);
                    }}
                  >
                    {t('inventory.add.recordAgain', 'Tout recommencer')}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <Alert color="gray">
              {t(
                'inventory.add.voiceUnsupported',
                'Ce navigateur ne permet pas de parler à Lucien : passez cette étape, Lucien regardera les photos.'
              )}
            </Alert>
          )}
          {hint && <Alert color="orange">{hint}</Alert>}
          <Button
            size="lg"
            fullWidth
            onClick={() => {
              if (isRecording) {
                setHint(t('inventory.add.stopFirst', 'Appuyez d’abord sur « Arrêter ».'));
                return;
              }
              if (!recordings.length) {
                setHint(
                  t(
                    'inventory.add.voiceRequired',
                    'Appuyez sur le micro et dites quelques mots, ou passez cette étape.'
                  )
                );
                return;
              }
              askLucien(recordings);
            }}
          >
            {t('inventory.add.voiceDone', 'J’ai fini')}
          </Button>
          <Button
            variant="outline"
            size="md"
            onClick={() => {
              if (isRecording) stop();
              setRecordings([]);
              askLucien([]);
            }}
          >
            {t('inventory.add.voiceSkip', 'Passer, je n’ai rien à dire')}
          </Button>
        </section>
      )}

      {step === 'thinking' && (
        <section className={`${styles.step} ${styles.thinking}`}>
          <Loader size="lg" />
          <PageHeader label={t('inventory.add.thinking', 'Lucien prépare la fiche…')} />
          <Text className={styles.sub}>
            {t(
              'inventory.add.thinkingHint',
              'Il regarde les photos, lit l’étiquette, écoute ce que vous avez dit et cherche le prix neuf.'
            )}
          </Text>
        </section>
      )}

      {step === 'review' && (
        <section className={styles.step}>
          <PageHeader label={t('inventory.add.reviewTitle', 'Voici ce que j’ai compris')} />
          <Text className={styles.sub}>
            {t('inventory.add.reviewHint', 'Corrigez ce qui ne va pas, puis enregistrez.')}
          </Text>
          {draftError && <Alert color="gray">{draftError}</Alert>}
          <div className={styles.strip}>
            {photos.map(photo => (
              <img
                key={photo.key}
                src={photo.serverFile?.previewMediumUrl ?? photo.serverFile?.url}
                alt=""
              />
            ))}
          </div>

          {draft?.unknownRoomName && (
            <Alert color="teal">
              <div className={styles.alertContent}>
                <span>
                  {t(
                    'inventory.add.unknownRoom',
                    'Vous avez dit « {{name}} », qui n’existe pas encore dans la maison.',
                    { name: draft.unknownRoomName }
                  )}
                </span>
                <Button size="sm" loading={isCreatingRoom} onClick={createSpokenRoom}>
                  {t('inventory.add.createRoom', 'Créer la pièce {{name}}', {
                    name: draft.unknownRoomName,
                  })}
                </Button>
              </div>
            </Alert>
          )}

          <InventoryItemFields
            values={values}
            onChange={patch => setValues(current => ({ ...current, ...patch }))}
            roomOptions={roomOptions}
            valuesVisible
            rule={rule}
            sources={draft?.sources}
            nameError={error && !values.name.trim() ? error : null}
          />

          {draft?.suggestedReplacementValue &&
            !webPriceAnswered &&
            values.replacementValueSource !== InventoryValueSource.Manual && (
              <div className={styles.match}>
                <span className={styles.matchLabel}>
                  <IconSparkles size={16} /> {t('inventory.add.lookalike', 'Ça ressemble à')}
                </span>
                <span className={styles.matchWhat}>{draft.suggestedReplacementValueLabel}</span>
                <span>
                  {t('inventory.add.newToday', 'Neuf aujourd’hui : {{price}}', {
                    price: formatEuros(draft.suggestedReplacementValue),
                  })}
                  {draft.suggestedReplacementValueUrl && (
                    <>
                      {' · '}
                      <a href={draft.suggestedReplacementValueUrl} target="_blank" rel="noreferrer">
                        {t('inventory.add.source', 'voir la page')}
                      </a>
                    </>
                  )}
                </span>
                <div className={styles.row2}>
                  <Button size="md" onClick={acceptWebPrice}>
                    {t('inventory.add.keepPrice', 'Garder ce prix')}
                  </Button>
                  <Button size="md" variant="outline" onClick={() => setWebPriceAnswered(true)}>
                    {t('inventory.add.notThisOne', 'Non')}
                  </Button>
                </div>
              </div>
            )}

          {(taskTitle || memoryTitle || otherSuggestions.length > 0) && (
            <div className={styles.group}>
              <Text className={styles.groupTitle}>
                {t('inventory.add.lucienProposes', 'Lucien vous propose aussi')}
              </Text>
              {memoryTitle !== null && (
                <div className={styles.suggestion}>
                  <Checkbox
                    size="md"
                    checked={memoryChecked}
                    onChange={event => setMemoryChecked(event.currentTarget.checked)}
                    label={
                      recordings.length
                        ? t(
                            'inventory.add.memoryWithVoice',
                            'Ajouter un souvenir à la Mémoire de la maison (votre voix est gardée avec)'
                          )
                        : t('inventory.add.memory', 'Ajouter un souvenir à la Mémoire de la maison')
                    }
                  />
                  {memoryChecked && (
                    <TextInput
                      size="md"
                      aria-label={t('inventory.add.memoryTitle', 'Titre du souvenir')}
                      value={memoryTitle}
                      onChange={event => setMemoryTitle(event.currentTarget.value)}
                    />
                  )}
                  {memoryChecked && toldDate && (
                    <div className={styles.match}>
                      <span className={styles.matchLabel}>
                        <IconSparkles size={16} />{' '}
                        {t('inventory.add.memoryWhen', 'Ce souvenir date de quand ?')}
                      </span>
                      <div className={styles.row2}>
                        <Button
                          size="md"
                          variant={useToldDate ? 'filled' : 'outline'}
                          aria-pressed={useToldDate}
                          onClick={() => setUseToldDate(true)}
                        >
                          {toldDate}
                        </Button>
                        <Button
                          size="md"
                          variant={useToldDate ? 'outline' : 'filled'}
                          aria-pressed={!useToldDate}
                          onClick={() => setUseToldDate(false)}
                        >
                          {t('inventory.add.memoryToday', 'Aujourd’hui')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {taskTitle !== null && (
                <div className={styles.suggestion}>
                  <Checkbox
                    size="md"
                    checked={taskChecked}
                    onChange={event => setTaskChecked(event.currentTarget.checked)}
                    label={t('inventory.add.task', 'Créer une action, pour vous, sans date')}
                  />
                  {taskChecked && (
                    <TextInput
                      size="md"
                      aria-label={t('inventory.add.taskTitle', 'Titre de l’action')}
                      value={taskTitle}
                      onChange={event => setTaskTitle(event.currentTarget.value)}
                    />
                  )}
                </div>
              )}
              {otherSuggestions.map(link => (
                <Checkbox
                  key={suggestionKey(link.type, link.id)}
                  size="md"
                  checked={checked.has(suggestionKey(link.type, link.id))}
                  onChange={() => toggle(suggestionKey(link.type, link.id))}
                  label={
                    link.type === LinkableEntityType.Expense
                      ? t('inventory.add.linkExpense', 'Relier la dépense « {{title}} »', {
                          title: link.title,
                        })
                      : t('inventory.add.linkContact', 'Relier le contact « {{title}} »', {
                          title: link.title,
                        })
                  }
                />
              ))}
            </div>
          )}

          <div className={styles.group}>
            <Text className={styles.groupTitle}>{t('inventory.add.invoices', 'Factures')}</Text>
            {documentSuggestions.length > 0 && (
              <>
                <Text size="sm" c="dimmed">
                  {t(
                    'inventory.add.inDocuments',
                    'Dans vos documents, seuls ceux cochés seront reliés :'
                  )}
                </Text>
                {documentSuggestions.map(link => (
                  <Checkbox
                    key={suggestionKey(link.type, link.id)}
                    size="md"
                    checked={checked.has(suggestionKey(link.type, link.id))}
                    onChange={() => toggle(suggestionKey(link.type, link.id))}
                    label={
                      link.reason
                        ? t('inventory.add.foundWords', '{{title}} (contient « {{words}} »)', {
                            title: link.title,
                            words: link.reason,
                          })
                        : link.title
                    }
                  />
                ))}
              </>
            )}
            <InventoryInvoices
              invoices={invoices}
              onAdd={invoice => setInvoices(current => [...current, invoice])}
              onRemove={id => setInvoices(current => current.filter(invoice => invoice.id !== id))}
            />
          </div>

          {error && values.name.trim() && <Alert color="red">{error}</Alert>}
          <Button size="lg" fullWidth loading={isSaving} onClick={save}>
            {t('inventory.add.save', 'Enregistrer')}
          </Button>
          <div className={styles.row2}>
            <Button variant="outline" size="md" onClick={() => goTo('voice')}>
              {t('common.back', 'Retour')}
            </Button>
            <Button variant="outline" size="md" onClick={() => navigate(listPath)}>
              {t('common.cancel', 'Annuler')}
            </Button>
          </div>
        </section>
      )}

      {step === 'saved' && created && (
        <section className={`${styles.step} ${styles.saved}`}>
          <div className={styles.okMark}>
            <IconCheck size={48} />
          </div>
          <PageHeader
            label={t('inventory.add.savedTitle', '{{name}} enregistré', { name: created.name })}
          />
          <div className={styles.card}>
            {created.room && (
              <div className={styles.kv}>
                <span>{t('inventory.fields.room', 'Pièce')}</span>
                <b>{created.room.name}</b>
              </div>
            )}
            {created.currentValue != null && (
              <div className={styles.kv}>
                <span>{t('inventory.fields.currentValue', 'Vaut aujourd’hui, à peu près')}</span>
                <b>{formatEuros(created.currentValue)}</b>
              </div>
            )}
            {created.createdMemoryId && (
              <div className={styles.kv}>
                <span>{t('inventory.add.memoryAdded', 'Souvenir ajouté')}</span>
                <b>{t('memories.title', 'La mémoire de la maison')}</b>
              </div>
            )}
            {created.createdTaskId && (
              <div className={styles.kv}>
                <span>{t('inventory.add.taskCreated', 'Action créée')}</span>
                <b>{taskTitle}</b>
              </div>
            )}
            {created.invoices.length > 0 && (
              <div className={styles.kv}>
                <span>{t('inventory.add.invoicesLinked', 'Factures reliées')}</span>
                <b>{created.invoices.length}</b>
              </div>
            )}
            <div className={styles.kv}>
              <span>{t('inventory.add.timeSpent', 'Temps pour cet objet')}</span>
              <b>{t('inventory.add.seconds', '{{count}} s', { count: secondsSpent })}</b>
            </div>
          </div>
          <Button size="lg" fullWidth onClick={() => nextObject(true)}>
            {created.room
              ? t('inventory.add.nextSameRoom', 'Objet suivant dans {{room}}', {
                  room: created.room.name,
                })
              : t('inventory.add.next', 'Objet suivant')}
          </Button>
          <div className={styles.row2}>
            <Button variant="outline" size="md" onClick={() => nextObject(false)}>
              {t('inventory.add.otherRoom', 'Autre pièce')}
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate(`${listPath}/${created.id}`)}
            >
              {t('inventory.add.seeItem', 'Voir la fiche')}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
};

export default InventoryAddPage;
